import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  FaHome,
  FaBars,
  FaSignOutAlt,
  FaProjectDiagram,
  FaChartBar,
  FaUsers,
  FaShieldAlt,
} from "react-icons/fa";
import axios from "axios";
import Chart from "chart.js/auto";
import API_URL from "../config";
import { getStatusBadgeClass } from "../utils/statusBadge";
import {
  getLocalDevProjects,
  getLocalDevUsers,
  isLocalDevCompanyId,
} from "../utils/localDevAuth";
import {
  clearStoredAuth,
  getApiErrorMessage,
  getAuthHeaders,
  getLoginPath,
  getStoredAccessToken,
  handleExpiredSession,
} from "../utils/authSession";
// import { disconnectWebSocket } from '../websocket';

const AdminDashboard = ({
  user,
  showTrashAgent = false,
  dashboardLabel = "Admin Dashboard",
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedStartDate, setSelectedStartDate] = useState("");
  const [selectedEndDate, setSelectedEndDate] = useState("");
  const [appraisalReport, setAppraisalReport] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [editUserData, setEditUserData] = useState({});
  const [editProjectData, setEditProjectData] = useState({});
  const [securityAlerts, setSecurityAlerts] = useState([]);
  const [securitySummary, setSecuritySummary] = useState({
    total_alerts: 0,
    high_risk: 0,
    unique_browsers: 0,
  });
  const [browserUpdateStatus, setBrowserUpdateStatus] = useState(null);
  const [trashAgentSummary, setTrashAgentSummary] = useState({
    total_quarantined_files: 0,
    critical_count: 0,
    high_risk_count: 0,
    medium_risk_count: 0,
    suspicious_upload_count: 0,
  });
  const [latestQuarantinedFiles, setLatestQuarantinedFiles] = useState([]);
  const [firewallReport, setFirewallReport] = useState(null);
  const [firewallReportMessage, setFirewallReportMessage] = useState("");
  const [firewallReportStale, setFirewallReportStale] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const navigate = useNavigate();

  const ongoingProjectsRef = useRef(null);
  const completedProjectsRef = useRef(null);
  const appraisalReportRef = useRef(null);
  const userManagementRef = useRef(null);
  const securityAlertsRef = useRef(null);
  const chartRef = useRef(null);

  const isLocalDevSession =
    isLocalDevCompanyId(user?.company?.id) ||
    isLocalDevCompanyId(localStorage.getItem("company_id"));
  const effectiveCompanyId =
    user?.company?.id || localStorage.getItem("company_id") || "1";

  const redirectToLogin = useCallback(() => {
    handleExpiredSession({
      navigate,
      companyId: effectiveCompanyId,
    });
  }, [effectiveCompanyId, navigate]);

  const getRequiredToken = useCallback(() => {
    const token = getStoredAccessToken();
    if (!token) {
      throw new Error("SESSION_EXPIRED");
    }
    return token;
  }, []);

  const isUnauthorizedError = (requestError) =>
    requestError?.response?.status === 401 ||
    requestError?.message === "SESSION_EXPIRED";

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      localStorage.removeItem("cachedUsers");

      if (isLocalDevSession) {
        const localCompanyId =
          user?.company?.id || localStorage.getItem("company_id");
        setUsers(getLocalDevUsers(localCompanyId));
        setProjects(getLocalDevProjects(localCompanyId));
        setSecurityAlerts([]);
        setSecuritySummary({
          total_alerts: 0,
          high_risk: 0,
          unique_browsers: 0,
        });
        setBrowserUpdateStatus(null);
        setTrashAgentSummary({
          total_quarantined_files: 0,
          critical_count: 0,
          high_risk_count: 0,
          medium_risk_count: 0,
          suspicious_upload_count: 0,
        });
        setLatestQuarantinedFiles([]);
        setFirewallReport(null);
        setFirewallReportMessage(
          "Firewall report not generated yet. Run the server inspection command on Linux server.",
        );
        setFirewallReportStale(true);
        return;
      }

      const token = getRequiredToken();

      const [usersResponse, projectsResponse] = await Promise.all([
        axios.get(`${API_URL}/api/users/`, {
          headers: getAuthHeaders(token),
        }),
        axios.get(`${API_URL}/api/projects/`, {
          headers: getAuthHeaders(token),
        }),
      ]);

      const userCompanyId = user.company?.id ? String(user.company.id) : null;

      if (!userCompanyId) {
        throw new Error(
          "User company ID is not defined. Please ensure the user profile includes company information.",
        );
      }

      const companyUsers = userCompanyId
        ? usersResponse.data.filter((u) => {
            const uCompanyId = u.company?.id ? String(u.company.id) : null;
            return uCompanyId === userCompanyId;
          })
        : usersResponse.data;

      const companyProjects = userCompanyId
        ? projectsResponse.data.filter((p) => {
            const pCompanyId = p.company?.id ? String(p.company.id) : null;
            return pCompanyId === userCompanyId;
          })
        : projectsResponse.data;

      setUsers(companyUsers);
      setProjects(companyProjects);

      try {
        const securityResponse = await axios.get(
          `${API_URL}/api/browser-security/alerts/`,
          {
            headers: getAuthHeaders(token),
          },
        );
        setSecurityAlerts(
          Array.isArray(securityResponse.data?.alerts)
            ? securityResponse.data.alerts
            : [],
        );
        setSecuritySummary(
          securityResponse.data?.summary || {
            total_alerts: 0,
            high_risk: 0,
            unique_browsers: 0,
          },
        );
        setBrowserUpdateStatus(
          securityResponse.data?.browserUpdateStatus || null,
        );
      } catch (securityError) {
        setSecurityAlerts([]);
        setSecuritySummary({
          total_alerts: 0,
          high_risk: 0,
          unique_browsers: 0,
        });
        setBrowserUpdateStatus(null);
      }

      try {
        const trashResponse = await axios.get(
          `${API_URL}/api/trash-agent/summary/`,
          {
            headers: getAuthHeaders(token),
          },
        );
        setTrashAgentSummary(
          trashResponse.data?.summary || {
            total_quarantined_files: 0,
            critical_count: 0,
            high_risk_count: 0,
            medium_risk_count: 0,
            suspicious_upload_count: 0,
          },
        );
        setLatestQuarantinedFiles(
          Array.isArray(trashResponse.data?.latest_quarantined_files)
            ? trashResponse.data.latest_quarantined_files
            : [],
        );
      } catch (trashError) {
        setTrashAgentSummary({
          total_quarantined_files: 0,
          critical_count: 0,
          high_risk_count: 0,
          medium_risk_count: 0,
          suspicious_upload_count: 0,
        });
        setLatestQuarantinedFiles([]);
      }

      if (showTrashAgent && user?.is_superuser) {
        try {
          const firewallResponse = await axios.get(
            `${API_URL}/api/superadmin/firewall-exposure-report/`,
            {
              headers: getAuthHeaders(token),
            },
          );
          setFirewallReport(firewallResponse.data?.report || null);
          setFirewallReportMessage(firewallResponse.data?.detail || "");
          setFirewallReportStale(Boolean(firewallResponse.data?.is_stale));
        } catch (firewallError) {
          setFirewallReport(null);
          setFirewallReportMessage(
            getApiErrorMessage(firewallError, {
              fallback:
                "Firewall report not generated yet. Run the server inspection command on Linux server.",
            }),
          );
          setFirewallReportStale(true);
        }
      } else {
        setFirewallReport(null);
        setFirewallReportMessage("");
        setFirewallReportStale(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (isUnauthorizedError(error)) {
        setError("Session expired. Please log in again.");
        redirectToLogin();
      } else {
        setError(
          getApiErrorMessage(error, {
            fallback: "Failed to load dashboard data. Please try again.",
          }),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [
    user,
    isLocalDevSession,
    getRequiredToken,
    redirectToLogin,
    showTrashAgent,
  ]);

  const formatFirewallGeneratedTime = (value) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "Unknown" : date.toLocaleString();
  };

  useEffect(() => {
    setUsers([]);
    setProjects([]);
    fetchData();
  }, [fetchData]);

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen((prev) => !prev);
  };

  const toggleResponsiveSidebar = () => {
    if (window.matchMedia("(min-width: 1024px)").matches) {
      toggleSidebar();
      return;
    }

    toggleMobileSidebar();
  };

  const handleMobileNavClick = () => {
    setIsMobileSidebarOpen(false);
  };
  const sidebarIdentityInitial = (user.company?.name || "A")
    .trim()
    .charAt(0)
    .toUpperCase();
  const adminNavLinkClass = ({ isActive }) =>
    `w-full shell-nav-link ${isActive ? "shell-nav-link-active" : ""} ${!isSidebarOpen ? "lg:justify-center" : ""}`;
  const shellNavClass = `w-full shell-nav-link ${!isSidebarOpen ? "lg:justify-center" : ""}`;
  const getRoleBadgeClass = (role) => {
    if (role === "admin") return "admin-badge admin-badge-role-admin";
    if (role === "manager") return "admin-badge admin-badge-role-manager";
    return "admin-badge admin-badge-role-team";
  };
  const getRiskBadgeClass = (riskLevel) => {
    if (riskLevel === "high" || riskLevel === "critical")
      return "admin-badge security-risk-high";
    if (riskLevel === "low") return "admin-badge security-risk-low";
    return "admin-badge security-risk-medium";
  };
  const formatSecurityTime = (value) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "Unknown" : date.toLocaleString();
  };
  const handleLogout = () => {
    clearStoredAuth();
    navigate(getLoginPath({ companyId: effectiveCompanyId }), {
      replace: true,
    });
  };

  const handleUserSelect = async (e) => {
    const userId = e.target.value;
    setSelectedUser(userId);
    fetchAppraisalReport(userId, selectedStartDate, selectedEndDate);
  };

  const handleStartDateChange = (e) => {
    setSelectedStartDate(e.target.value);
    fetchAppraisalReport(selectedUser, e.target.value, selectedEndDate);
  };

  const handleEndDateChange = (e) => {
    setSelectedEndDate(e.target.value);
    fetchAppraisalReport(selectedUser, selectedStartDate, e.target.value);
  };

  const openPrintableAppraisalReport = (report) => {
    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) {
      alert(
        "Unable to open the print preview. Please allow pop-ups and try again.",
      );
      return;
    }

    const dateRange =
      selectedStartDate && selectedEndDate
        ? `${selectedStartDate} to ${selectedEndDate}`
        : "All available dates";

    printWindow.document.write(`
            <!DOCTYPE html>
            <html>
                <head>
                    <title>Appraisal Report</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 32px; color: #111827; }
                        h1 { margin-bottom: 8px; }
                        p { margin: 6px 0; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th, td { border: 1px solid #d1d5db; padding: 10px; text-align: left; }
                        th { background: #f3f4f6; }
                    </style>
                </head>
                <body>
                    <h1>Appraisal Report</h1>
                    <p><strong>Name:</strong> ${report.name}</p>
                    <p><strong>User ID:</strong> ${report.user_id}</p>
                    <p><strong>Code:</strong> ${report.code}</p>
                    <p><strong>Role:</strong> ${report.role}</p>
                    <p><strong>Date Range:</strong> ${dateRange}</p>
                    <p><strong>Efficiency Score:</strong> ${report.efficiency_score}%</p>
                    <table>
                        <thead>
                            <tr>
                                <th>Metric</th>
                                <th>Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr><td>Total Tasks</td><td>${report.total_tasks}</td></tr>
                            <tr><td>Completed Tasks</td><td>${report.completed_tasks}</td></tr>
                            <tr><td>Ongoing Tasks</td><td>${report.ongoing_tasks}</td></tr>
                            <tr><td>Pending Approval Tasks</td><td>${report.pending_approval_tasks}</td></tr>
                            <tr><td>Overdue Tasks</td><td>${report.overdue_tasks}</td></tr>
                        </tbody>
                    </table>
                </body>
            </html>
        `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const fetchAppraisalReport = async (userId, startDate, endDate) => {
    if (userId) {
      if (isLocalDevSession) {
        const reportUser = getLocalDevUsers(
          user?.company?.id || localStorage.getItem("company_id"),
        ).find((u) => String(u.id) === String(userId));
        setAppraisalReport(
          reportUser
            ? {
                user_id: reportUser.id,
                name: reportUser.email.split("@")[0],
                code: reportUser.role === "manager" ? "M001" : "E001",
                role: reportUser.role,
                total_tasks: 5,
                completed_tasks: 3,
                ongoing_tasks: 1,
                pending_approval_tasks: 1,
                overdue_tasks: 0,
                efficiency_score: 86,
              }
            : null,
        );
        return;
      }

      try {
        const token = getRequiredToken();
        const params = { user_id: userId };
        if (startDate && endDate) {
          params.start_date = startDate;
          params.end_date = endDate;
        }
        const response = await axios.get(`${API_URL}/api/appraisal-report/`, {
          headers: getAuthHeaders(token),
          params: params,
        });
        setAppraisalReport(response.data[0] || null);
      } catch (error) {
        console.error("Error fetching appraisal report:", error);
        if (isUnauthorizedError(error)) {
          redirectToLogin();
        } else {
          alert(
            getApiErrorMessage(error, {
              fallback: "Failed to fetch appraisal report. Please try again.",
            }),
          );
        }
        setAppraisalReport(null);
      }
    } else {
      setAppraisalReport(null);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user.id);
    setEditUserData({
      role: user.role,
      designation: user.designation || "",
      mobile_number: user.mobile_number || "",
    });
  };

  const handleEditProject = (project) => {
    setEditingProject(project.id);
    setEditProjectData({
      name: project.name,
      description: project.description,
      start_date: project.start_date,
      end_date: project.end_date,
      manager: project.manager ? project.manager.id : "",
      status: project.status,
    });
  };

  const handleUserInputChange = (e) => {
    const { name, value } = e.target;
    setEditUserData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProjectInputChange = (e) => {
    const { name, value } = e.target;
    setEditProjectData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveUser = async (userId) => {
    try {
      const token = getRequiredToken();
      const userExists = users.find((u) => u.id === userId);
      if (!userExists) {
        throw new Error(
          "User not found in the current list. Please refresh the page.",
        );
      }

      const payload = {
        role: editUserData.role,
        designation: editUserData.designation || null,
        mobile_number: editUserData.mobile_number || null,
      };

      await axios.patch(`${API_URL}/api/users/${userId}/`, payload, {
        headers: {
          ...getAuthHeaders(token),
          "Content-Type": "application/json",
        },
      });
      alert("User updated successfully!");
      setEditingUser(null);
      fetchData();
    } catch (error) {
      console.error("Error updating user:", error);
      if (error.response?.status === 404) {
        alert(
          "User not found in the database. It may have been deleted or belongs to another company. Refreshing user list...",
        );
        fetchData();
      } else if (error.response?.status === 403) {
        alert(
          "You do not have permission or your company subscription is inactive.",
        );
      } else if (isUnauthorizedError(error)) {
        redirectToLogin();
      } else {
        alert(
          getApiErrorMessage(error, {
            fallback: "Failed to update user. Please try again.",
          }),
        );
      }
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        const token = getRequiredToken();
        const userExists = users.find((u) => u.id === userId);
        if (!userExists) {
          throw new Error(
            "User not found in the current list. Please refresh the page.",
          );
        }

        await axios.delete(`${API_URL}/api/users/${userId}/`, {
          headers: getAuthHeaders(token),
        });
        alert("User deleted successfully!");
        fetchData();
      } catch (error) {
        console.error("Error deleting user:", error);
        if (error.response?.status === 404) {
          alert(
            "User not found in the database. It may have been deleted or belongs to another company. Refreshing user list...",
          );
          fetchData();
        } else if (error.response?.status === 403) {
          alert(
            "You do not have permission or your company subscription is inactive.",
          );
        } else if (isUnauthorizedError(error)) {
          redirectToLogin();
        } else {
          alert(
            getApiErrorMessage(error, {
              fallback: "Failed to delete user. Please try again.",
            }),
          );
        }
      }
    }
  };

  const handleSaveProject = async (projectId) => {
    try {
      const token = getRequiredToken();
      await axios.patch(
        `${API_URL}/api/projects/${projectId}/`,
        editProjectData,
        {
          headers: getAuthHeaders(token),
        },
      );
      alert("Project updated successfully!");
      setEditingProject(null);
      fetchData();
    } catch (error) {
      console.error("Error updating project:", error);
      if (error.response?.status === 404) {
        alert("Project not found. Refreshing project list...");
        fetchData();
      } else if (error.response?.status === 403) {
        alert(
          "You do not have permission or your company subscription is inactive.",
        );
      } else if (isUnauthorizedError(error)) {
        redirectToLogin();
      } else {
        alert(
          getApiErrorMessage(error, {
            fallback: "Failed to update project. Please try again.",
          }),
        );
      }
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      try {
        const token = getRequiredToken();
        await axios.delete(`${API_URL}/api/projects/${projectId}/`, {
          headers: getAuthHeaders(token),
        });
        alert("Project deleted successfully!");
        fetchData();
      } catch (error) {
        console.error("Error deleting project:", error);
        if (error.response?.status === 404) {
          alert("Project not found. Refreshing project list...");
          fetchData();
        } else if (error.response?.status === 403) {
          alert(
            "You do not have permission or your company subscription is inactive.",
          );
        } else if (isUnauthorizedError(error)) {
          redirectToLogin();
        } else {
          alert(
            getApiErrorMessage(error, {
              fallback: "Failed to delete project. Please try again.",
            }),
          );
        }
      }
    }
  };

  const handleGenerateAppraisalReport = async () => {
    try {
      if (!selectedUser) {
        alert("Please select a user before generating the appraisal report.");
        return;
      }

      if (isLocalDevSession) {
        if (!appraisalReport) {
          alert("Please load the appraisal report first and then try again.");
          return;
        }

        openPrintableAppraisalReport(appraisalReport);
        return;
      }

      const token = getRequiredToken();

      const params = { user_id: selectedUser };
      if (selectedStartDate && selectedEndDate) {
        params.start_date = selectedStartDate;
        params.end_date = selectedEndDate;
      }
      const response = await axios.get(
        `${API_URL}/api/generate-appraisal-pdf/`,
        {
          headers: getAuthHeaders(token),
          params: params,
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "appraisal_report.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      alert("Appraisal report PDF generated successfully!");
    } catch (error) {
      console.error("Error generating appraisal report PDF:", error);
      if (isUnauthorizedError(error)) {
        redirectToLogin();
      } else if (!error.response) {
        alert(
          "Failed to generate appraisal report PDF: backend server is unavailable or the PDF endpoint could not be reached.",
        );
      } else {
        alert(
          getApiErrorMessage(error, {
            fallback:
              "Failed to generate appraisal report PDF. Please try again.",
          }),
        );
      }
    }
  };

  const scrollToSection = (ref) => {
    ref.current.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (appraisalReport && chartRef.current) {
      const ctx = chartRef.current.getContext("2d");
      if (chartRef.current.chart) chartRef.current.chart.destroy();
      chartRef.current.chart = new Chart(ctx, {
        type: "bar",
        data: {
          labels: ["Completed", "Ongoing", "Pending Approval", "Overdue"],
          datasets: [
            {
              label: "Task Status",
              data: [
                appraisalReport.completed_tasks,
                appraisalReport.ongoing_tasks,
                appraisalReport.pending_approval_tasks,
                appraisalReport.overdue_tasks,
              ],
              backgroundColor: ["#34D399", "#FBBF24", "#A855F7", "#F87171"],
            },
          ],
        },
        options: {
          scales: {
            y: {
              beginAtZero: true,
            },
          },
        },
      });
    }
  }, [appraisalReport]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background font-sans">
        <p className="text-text text-lg sm:text-xl">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background font-sans">
        <p className="text-red-500 text-lg sm:text-xl">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background font-sans relative overflow-x-hidden">
      {/* Mobile Overlay */}
      {isMobileSidebarOpen && (
        <div
          className="overlay-backdrop z-30 lg:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`shell-sidebar transition-all duration-300 fixed top-0 left-0 h-full z-40 flex flex-col 
                w-64 ${isMobileSidebarOpen ? "translate-x-0" : "-translate-x-full"} 
                lg:translate-x-0 ${isSidebarOpen ? "lg:w-80" : "lg:w-20"}`}
      >
        <div
          className={`border-b border-border px-3 py-4 sm:px-4 ${!isSidebarOpen ? "lg:px-3" : ""}`}
        >
          <div
            className={`relative flex items-start justify-between gap-3 ${!isSidebarOpen ? "lg:justify-center" : ""}`}
          >
            <div
              className={`flex min-w-0 items-center gap-3 ${!isSidebarOpen ? "lg:hidden" : ""}`}
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow-btn">
                <span className="text-sm font-semibold sm:text-base">
                  {sidebarIdentityInitial}
                </span>
              </div>
              <div className="min-w-0">
                <p className="truncate text-base font-semibold text-text sm:text-lg">
                  {user.company ? user.company.name : "No Company"}
                </p>
                <p className="text-xs text-secondary-text sm:text-sm">
                  Admin Panel
                </p>
              </div>
            </div>
            <div
              className={`hidden h-10 w-10 items-center justify-center rounded-full bg-primary text-white shadow-btn ${!isSidebarOpen ? "lg:flex" : ""}`}
            >
              <span className="text-sm font-semibold">
                {sidebarIdentityInitial}
              </span>
            </div>
            <div
              className={`${!isSidebarOpen ? "lg:absolute lg:right-3 lg:top-4" : ""}`}
            >
              {/* Desktop Toggle */}
              <button
                onClick={toggleSidebar}
                className="hidden rounded-xl p-2 text-secondary-text transition hover:bg-primary/5 hover:text-primary focus:outline-none lg:block"
              >
                <FaBars size={16} className="text-base sm:text-lg md:text-xl" />
              </button>
              {/* Mobile Close */}
              <button
                onClick={toggleMobileSidebar}
                className="ml-auto rounded-xl p-2 text-secondary-text transition hover:bg-primary/5 hover:text-primary focus:outline-none lg:hidden"
              >
                <FaBars size={16} className="text-base sm:text-lg" />
              </button>
            </div>
          </div>
        </div>

        <nav className="flex justify-between items-center mt-8 sm:mt-3 md:mt-4">
          <ul className="flex flex-col items-stretch justify-between w-full">
            <li className="w-full">
              <NavLink
                end
                to="/admin"
                onClick={handleMobileNavClick}
                className={adminNavLinkClass}
              >
                <FaHome
                  className={`mr-2 sm:mr-3 ${!isSidebarOpen ? "lg:mr-0" : ""}`}
                  size={16}
                />
                <span
                  className={`text-sm sm:text-base ${!isSidebarOpen ? "lg:hidden" : ""}`}
                >
                  {dashboardLabel}
                </span>
              </NavLink>
            </li>
            <li
              className={shellNavClass}
              onClick={() => {
                scrollToSection(ongoingProjectsRef);
                handleMobileNavClick();
              }}
            >
              <FaProjectDiagram
                className={`mr-2 sm:mr-3 ${!isSidebarOpen ? "lg:mr-0" : ""}`}
                size={16}
              />
              <span
                className={`text-sm sm:text-base ${!isSidebarOpen ? "lg:hidden" : ""}`}
              >
                Ongoing Projects
              </span>
            </li>
            <li
              className={shellNavClass}
              onClick={() => {
                scrollToSection(completedProjectsRef);
                handleMobileNavClick();
              }}
            >
              <FaProjectDiagram
                className={`mr-2 sm:mr-3 ${!isSidebarOpen ? "lg:mr-0" : ""}`}
                size={16}
              />
              <span
                className={`text-sm sm:text-base ${!isSidebarOpen ? "lg:hidden" : ""}`}
              >
                Completed Projects
              </span>
            </li>
            <li
              className={shellNavClass}
              onClick={() => {
                scrollToSection(appraisalReportRef);
                handleMobileNavClick();
              }}
            >
              <FaChartBar
                className={`mr-2 sm:mr-3 ${!isSidebarOpen ? "lg:mr-0" : ""}`}
                size={16}
              />
              <span
                className={`text-sm sm:text-base ${!isSidebarOpen ? "lg:hidden" : ""}`}
              >
                Appraisal Report
              </span>
            </li>
            <li
              className={shellNavClass}
              onClick={() => {
                scrollToSection(securityAlertsRef);
                handleMobileNavClick();
              }}
            >
              <FaShieldAlt
                className={`mr-2 sm:mr-3 ${!isSidebarOpen ? "lg:mr-0" : ""}`}
                size={16}
              />
              <span
                className={`text-sm sm:text-base ${!isSidebarOpen ? "lg:hidden" : ""}`}
              >
                Security Alerts
              </span>
            </li>
            <li
              className={shellNavClass}
              onClick={() => {
                scrollToSection(userManagementRef);
                handleMobileNavClick();
              }}
            >
              <FaUsers
                className={`mr-2 sm:mr-3 ${!isSidebarOpen ? "lg:mr-0" : ""}`}
                size={16}
              />
              <span
                className={`text-sm sm:text-base ${!isSidebarOpen ? "lg:hidden" : ""}`}
              >
                User Management
              </span>
            </li>
          </ul>
        </nav>
      </div>
      <div
        className={`min-w-0 flex-1 transition-all duration-300 ${isSidebarOpen ? "lg:ml-80" : "lg:ml-20"}`}
      >
        {/* Navbar */}
        <div
          className={`shell-topbar p-2 sm:p-3 md:p-4 flex items-center justify-between fixed top-0 right-0 z-30 transition-all duration-300 left-0 ${isSidebarOpen ? "lg:left-80" : "lg:left-20"}`}
        >
          <div className="flex items-center">
            {/* Mobile Toggle */}
            <button
              type="button"
              onClick={toggleResponsiveSidebar}
              className="mr-3 rounded-xl p-2 text-secondary-text transition hover:bg-primary/5 hover:text-primary focus:outline-none"
              aria-label="Toggle sidebar"
              aria-expanded={isMobileSidebarOpen || isSidebarOpen}
            >
              <FaBars size={20} />
            </button>
            <span className="text-text text-lg sm:text-xl font-semibold truncate lg:hidden">
              {user.company ? user.company.name : "No Company"}
            </span>
          </div>
          <div className="flex items-center">
            <button
              onClick={handleLogout}
              className="secondary-btn flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm"
            >
              <FaSignOutAlt className="text-base sm:text-lg md:text-xl" />
              <span>Logout</span>
            </button>
          </div>
        </div>
        <div className="mt-16 overflow-x-hidden p-4 sm:p-5 md:p-6">
          <h1 className="mb-4 break-words text-xl font-bold leading-tight sm:mb-6 sm:text-2xl">
            {dashboardLabel} - Welcome {user.email || "Admin"}
          </h1>

          {/* User Management Section */}
          <div ref={userManagementRef} className="mb-6 sm:mb-8">
            <div className="mb-3 flex flex-col gap-3 sm:mb-4 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-lg font-semibold sm:text-xl">
                User Management
              </h2>
              <Link to="/admin/create-user">
                <button className="primary-btn p-2 text-xs font-medium sm:p-3 sm:text-sm">
                  Create User
                </button>
              </Link>
            </div>
            <h3 className="mb-3 text-base font-bold sm:mb-4 sm:text-lg">
              All Users
            </h3>
            {users.length === 0 ? (
              <p className="text-text italic text-xs sm:text-sm">
                No users available.
              </p>
            ) : (
              <div className="admin-table-shell overflow-x-auto">
                <table className="admin-table min-w-max w-full">
                  <thead>
                    <tr>
                      <th className="min-w-[120px] sm:min-w-[150px]">ID</th>
                      <th className="min-w-[120px] sm:min-w-[150px]">Email</th>
                      <th className="min-w-[120px] sm:min-w-[150px]">Role</th>
                      <th className="min-w-[120px] sm:min-w-[150px]">Code</th>
                      <th className="min-w-[120px] sm:min-w-[150px] font-bold">
                        Designation
                      </th>
                      <th className="min-w-[120px] sm:min-w-[150px]">
                        Manager
                      </th>
                      <th className="min-w-[120px] sm:min-w-[150px]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id}>
                        {editingUser === u.id ? (
                          <>
                            <td>{u.id}</td>
                            <td>{u.email}</td>
                            <td>
                              <select
                                name="role"
                                value={editUserData.role}
                                onChange={handleUserInputChange}
                                className="input-field w-full p-1 text-text text-xs sm:p-2 sm:text-sm"
                              >
                                <option value="admin">Admin</option>
                                <option value="manager">Manager</option>
                                <option value="team_member">Team Member</option>
                              </select>
                            </td>
                            <td>{u.code || "N/A"}</td>
                            <td>
                              <input
                                type="text"
                                name="designation"
                                value={editUserData.designation}
                                onChange={handleUserInputChange}
                                className="input-field w-full p-1 text-text text-xs sm:p-2 sm:text-sm"
                              />
                            </td>
                            <td>{u.manager_data?.email || "N/A"}</td>
                            <td>
                              <div className="table-actions">
                                <button
                                  onClick={() => handleSaveUser(u.id)}
                                  className="primary-btn px-2 py-1 text-xs sm:px-4 sm:py-2 sm:text-sm"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={() => setEditingUser(null)}
                                  className="secondary-btn px-2 py-1 text-xs sm:px-4 sm:py-2 sm:text-sm"
                                >
                                  Cancel
                                </button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>{u.id}</td>
                            <td>{u.email}</td>
                            <td>
                              <span className={getRoleBadgeClass(u.role)}>
                                {u.role}
                              </span>
                            </td>
                            <td>{u.code || "N/A"}</td>
                            <td className="font-semibold">
                              {u.designation || "N/A"}
                            </td>
                            <td>{u.manager_data?.email || "N/A"}</td>
                            <td>
                              <div className="table-actions">
                                <button
                                  onClick={() => handleEditUser(u)}
                                  className="table-edit-btn"
                                  disabled={u.id === user.id}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(u.id)}
                                  className="table-delete-btn"
                                  disabled={u.id === user.id}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Ongoing Projects Section */}
          <div ref={ongoingProjectsRef} className="mb-6 sm:mb-8">
            <div className="mb-3 flex flex-col gap-3 sm:mb-4 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-lg font-semibold sm:text-xl">
                Ongoing Projects
              </h2>
              <Link to="/admin/create-project">
                <button className="primary-btn p-2 text-xs font-medium sm:p-3 sm:text-sm">
                  Create Project
                </button>
              </Link>
            </div>
            <h3 className="mb-3 text-base font-bold sm:mb-4 sm:text-lg">
              Ongoing Projects Overview
            </h3>
            {projects.filter((project) => project.status === "ongoing")
              .length === 0 ? (
              <p className="text-text italic text-xs sm:text-sm">
                No ongoing projects available.
              </p>
            ) : (
              <div className="admin-table-shell overflow-x-auto">
                <table className="admin-table min-w-max w-full">
                  <thead>
                    <tr>
                      <th className="min-w-[120px] sm:min-w-[150px]">Name</th>
                      <th className="min-w-[120px] sm:min-w-[150px]">
                        Manager Email
                      </th>
                      <th className="min-w-[120px] sm:min-w-[150px]">
                        Company
                      </th>
                      <th className="min-w-[120px] sm:min-w-[150px]">Status</th>
                      <th className="min-w-[120px] sm:min-w-[150px]">
                        Start Date
                      </th>
                      <th className="min-w-[120px] sm:min-w-[150px]">
                        End Date
                      </th>
                      <th className="min-w-[120px] sm:min-w-[150px]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects
                      .filter((project) => project.status === "ongoing")
                      .map((project) => (
                        <tr key={project.id}>
                          {editingProject === project.id ? (
                            <>
                              <td>
                                <input
                                  type="text"
                                  name="name"
                                  value={editProjectData.name}
                                  onChange={handleProjectInputChange}
                                  className="input-field w-full p-1 text-text text-xs sm:p-2 sm:text-sm"
                                />
                              </td>
                              <td>
                                {project.manager
                                  ? project.manager.email
                                  : "N/A"}
                              </td>
                              <td>
                                {project.company ? project.company.name : "N/A"}
                              </td>
                              <td>
                                <select
                                  name="status"
                                  value={editProjectData.status}
                                  onChange={handleProjectInputChange}
                                  className="input-field w-full p-1 text-text text-xs sm:p-2 sm:text-sm"
                                >
                                  <option value="ongoing">Ongoing</option>
                                  <option value="completed">Completed</option>
                                </select>
                              </td>
                              <td>
                                <input
                                  type="date"
                                  name="start_date"
                                  value={editProjectData.start_date}
                                  onChange={handleProjectInputChange}
                                  className="input-field w-full p-1 text-text text-xs sm:p-2 sm:text-sm"
                                />
                              </td>
                              <td>
                                <input
                                  type="date"
                                  name="end_date"
                                  value={editProjectData.end_date}
                                  onChange={handleProjectInputChange}
                                  className="input-field w-full p-1 text-text text-xs sm:p-2 sm:text-sm"
                                />
                              </td>
                              <td>
                                <div className="table-actions">
                                  <button
                                    onClick={() =>
                                      handleSaveProject(project.id)
                                    }
                                    className="primary-btn px-2 py-1 text-xs sm:px-4 sm:py-2 sm:text-sm"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setEditingProject(null)}
                                    className="secondary-btn px-2 py-1 text-xs sm:px-4 sm:py-2 sm:text-sm"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td>{project.name}</td>
                              <td>
                                {project.manager
                                  ? project.manager.email
                                  : "N/A"}
                              </td>
                              <td>
                                {project.company ? project.company.name : "N/A"}
                              </td>
                              <td>
                                <span
                                  className={getStatusBadgeClass(
                                    project.status,
                                  )}
                                >
                                  {project.status}
                                </span>
                              </td>
                              <td>{project.start_date}</td>
                              <td>{project.end_date}</td>
                              <td>
                                <div className="table-actions">
                                  <button
                                    onClick={() => handleEditProject(project)}
                                    className="table-edit-btn"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleDeleteProject(project.id)
                                    }
                                    className="table-delete-btn"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Completed Projects Section */}
          <div ref={completedProjectsRef} className="mb-6 sm:mb-8">
            <h2 className="mb-3 text-lg font-semibold sm:mb-4 sm:text-xl">
              Completed Projects
            </h2>
            {projects.filter((project) => project.status === "completed")
              .length === 0 ? (
              <p className="text-text italic text-xs sm:text-sm">
                No completed projects available.
              </p>
            ) : (
              <div className="admin-table-shell overflow-x-auto">
                <table className="admin-table min-w-max w-full">
                  <thead>
                    <tr>
                      <th className="min-w-[120px] sm:min-w-[150px]">Name</th>
                      <th className="min-w-[120px] sm:min-w-[150px]">
                        Manager Email
                      </th>
                      <th className="min-w-[120px] sm:min-w-[150px]">
                        Company
                      </th>
                      <th className="min-w-[120px] sm:min-w-[150px]">Status</th>
                      <th className="min-w-[120px] sm:min-w-[150px]">
                        Start Date
                      </th>
                      <th className="min-w-[120px] sm:min-w-[150px]">
                        End Date
                      </th>
                      <th className="min-w-[120px] sm:min-w-[150px]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects
                      .filter((project) => project.status === "completed")
                      .map((project) => (
                        <tr key={project.id}>
                          {editingProject === project.id ? (
                            <>
                              <td>
                                <input
                                  type="text"
                                  name="name"
                                  value={editProjectData.name}
                                  onChange={handleProjectInputChange}
                                  className="input-field w-full p-1 text-text text-xs sm:p-2 sm:text-sm"
                                />
                              </td>
                              <td>
                                {project.manager
                                  ? project.manager.email
                                  : "N/A"}
                              </td>
                              <td>
                                {project.company ? project.company.name : "N/A"}
                              </td>
                              <td>
                                <select
                                  name="status"
                                  value={editProjectData.status}
                                  onChange={handleProjectInputChange}
                                  className="input-field w-full p-1 text-text text-xs sm:p-2 sm:text-sm"
                                >
                                  <option value="ongoing">Ongoing</option>
                                  <option value="completed">Completed</option>
                                </select>
                              </td>
                              <td>
                                <input
                                  type="date"
                                  name="start_date"
                                  value={editProjectData.start_date}
                                  onChange={handleProjectInputChange}
                                  className="input-field w-full p-1 text-text text-xs sm:p-2 sm:text-sm"
                                />
                              </td>
                              <td>
                                <input
                                  type="date"
                                  name="end_date"
                                  value={editProjectData.end_date}
                                  onChange={handleProjectInputChange}
                                  className="input-field w-full p-1 text-text text-xs sm:p-2 sm:text-sm"
                                />
                              </td>
                              <td>
                                <div className="table-actions">
                                  <button
                                    onClick={() =>
                                      handleSaveProject(project.id)
                                    }
                                    className="primary-btn px-2 py-1 text-xs sm:px-4 sm:py-2 sm:text-sm"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setEditingProject(null)}
                                    className="secondary-btn px-2 py-1 text-xs sm:px-4 sm:py-2 sm:text-sm"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td>{project.name}</td>
                              <td>
                                {project.manager
                                  ? project.manager.email
                                  : "N/A"}
                              </td>
                              <td>
                                {project.company ? project.company.name : "N/A"}
                              </td>
                              <td>
                                <span
                                  className={getStatusBadgeClass(
                                    project.status,
                                  )}
                                >
                                  {project.status}
                                </span>
                              </td>
                              <td>{project.start_date}</td>
                              <td>{project.end_date}</td>
                              <td>
                                <div className="table-actions">
                                  <button
                                    onClick={() => handleEditProject(project)}
                                    className="table-edit-btn"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleDeleteProject(project.id)
                                    }
                                    className="table-delete-btn"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Security Alerts Section */}
          <div ref={securityAlertsRef} className="mb-6 sm:mb-8">
            <div className="mb-3 flex flex-col gap-2 sm:mb-4">
              <h2 className="text-lg font-semibold sm:text-xl">
                Security Alerts
              </h2>
              <p className="text-xs leading-6 text-secondary-text sm:text-sm">
                Browser errors are sanitized and grouped by browser, version,
                route, and message so admins can spot compatibility or
                suspicious frontend failures.
              </p>
            </div>

            <div className="mb-4 grid gap-3 sm:grid-cols-3">
              <div className="security-metric-card">
                <span>Total Reports</span>
                <strong>{securitySummary.total_alerts || 0}</strong>
              </div>
              <div className="security-metric-card">
                <span>High Risk</span>
                <strong>{securitySummary.high_risk || 0}</strong>
              </div>
              <div className="security-metric-card">
                <span>Browser Versions</span>
                <strong>{securitySummary.unique_browsers || 0}</strong>
              </div>
            </div>

            {browserUpdateStatus?.minimumSupported && (
              <div className="alert-info mb-4">
                Minimum supported browser versions: Chrome{" "}
                {browserUpdateStatus.minimumSupported.Chrome}, Edge{" "}
                {browserUpdateStatus.minimumSupported.Edge}, Firefox{" "}
                {browserUpdateStatus.minimumSupported.Firefox}, Safari{" "}
                {browserUpdateStatus.minimumSupported.Safari}.
              </div>
            )}

            {securityAlerts.length === 0 ? (
              <p className="text-text italic text-xs sm:text-sm">
                No browser security alerts captured yet.
              </p>
            ) : (
              <div className="admin-table-shell overflow-x-auto">
                <table className="admin-table min-w-max w-full">
                  <thead>
                    <tr>
                      <th className="min-w-[120px] sm:min-w-[150px]">
                        Browser
                      </th>
                      <th className="min-w-[120px] sm:min-w-[150px]">
                        Version
                      </th>
                      <th className="min-w-[120px] sm:min-w-[150px]">OS</th>
                      <th className="min-w-[120px] sm:min-w-[150px]">Route</th>
                      <th className="min-w-[120px] sm:min-w-[150px]">
                        Reports
                      </th>
                      <th className="min-w-[120px] sm:min-w-[150px]">Risk</th>
                      <th className="min-w-[120px] sm:min-w-[150px]">
                        Last Seen
                      </th>
                      <th className="min-w-[120px] sm:min-w-[150px]">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {securityAlerts.map((alert) => (
                      <tr key={alert.id}>
                        <td>{alert.browser_name || "unknown"}</td>
                        <td>{alert.browser_version || "unknown"}</td>
                        <td>{alert.os || "unknown"}</td>
                        <td>{alert.route || "-"}</td>
                        <td>{alert.error_count || 0}</td>
                        <td>
                          <span className={getRiskBadgeClass(alert.risk_level)}>
                            {alert.risk_level || "medium"}
                          </span>
                        </td>
                        <td>{formatSecurityTime(alert.last_seen_at)}</td>
                        <td>
                          {alert.suggested_action || "Monitor and review."}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {showTrashAgent && (
            <div className="mb-6 sm:mb-8">
              <div className="mb-3 flex flex-col gap-2 sm:mb-4">
                <h2 className="text-lg font-semibold sm:text-xl">
                  PutPens Trash Agent
                </h2>
                <p className="text-xs leading-6 text-secondary-text sm:text-sm">
                  Virus File Handling Agent tracks suspicious uploads that were
                  blocked and moved to quarantine instead of normal task
                  attachments.
                </p>
              </div>

              <div className="mb-4 grid gap-3 sm:grid-cols-4">
                <div className="security-metric-card">
                  <span>Total Delete</span>
                  <strong>
                    {trashAgentSummary.total_quarantined_files || 0}
                  </strong>
                </div>
                <div className="security-metric-card">
                  <span>Critical Files</span>
                  <strong>{trashAgentSummary.critical_count || 0}</strong>
                </div>
                <div className="security-metric-card">
                  <span>High Risk</span>
                  <strong>{trashAgentSummary.high_risk_count || 0}</strong>
                </div>
                <div className="security-metric-card">
                  <span>Suspicious Uploads</span>
                  <strong>
                    {trashAgentSummary.suspicious_upload_count || 0}
                  </strong>
                </div>
              </div>

              {latestQuarantinedFiles.length === 0 ? (
                <p className="text-text italic text-xs sm:text-sm">
                  No quarantined files recorded yet.
                </p>
              ) : (
                <div className="admin-table-shell overflow-x-auto">
                  <table className="admin-table min-w-max w-full">
                    <thead>
                      <tr>
                        <th className="min-w-[150px]">File</th>
                        <th className="min-w-[120px]">Risk</th>
                        <th className="min-w-[120px]">Source</th>
                        <th className="min-w-[160px]">Reason</th>
                        <th className="min-w-[160px]">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {latestQuarantinedFiles.map((item) => (
                        <tr key={item.id}>
                          <td>{item.original_file_name}</td>
                          <td>
                            <span
                              className={getRiskBadgeClass(
                                (item.risk_level || "").toLowerCase(),
                              )}
                            >
                              {item.risk_level}
                            </span>
                          </td>
                          <td>{item.source_type || "unknown"}</td>
                          <td>
                            {item.detection_reason ||
                              "Suspicious upload detected."}
                          </td>
                          <td>{item.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {showTrashAgent && user?.is_superuser && (
            <div className="mb-6 sm:mb-8">
              <div className="mb-3 flex flex-col gap-2 sm:mb-4">
                <h2 className="text-lg font-semibold sm:text-xl">
                  Firewall Exposure Visibility
                </h2>
                <p className="text-xs leading-6 text-secondary-text sm:text-sm">
                  Superadmin-only server exposure report loaded from a fixed
                  backend JSON file. This panel does not execute shell commands
                  from the browser.
                </p>
              </div>

              {firewallReport?.generated_at && (
                <div className="alert-info mb-4">
                  Report generated:{" "}
                  {formatFirewallGeneratedTime(firewallReport.generated_at)}
                  {firewallReportStale
                    ? " | Warning: report may be stale."
                    : ""}
                </div>
              )}

              {!firewallReport && firewallReportMessage ? (
                <div className="alert-info mb-4">{firewallReportMessage}</div>
              ) : (
                firewallReport && (
                  <>
                    {firewallReportStale && (
                      <div className="alert-info mb-4">
                        Warning: this firewall report is older than 24 hours.
                        Generate a fresh report on the Linux server.
                      </div>
                    )}

                    <div className="mb-4 grid gap-3 sm:grid-cols-4">
                      <div className="security-metric-card">
                        <span>Public Ports Found</span>
                        <strong>
                          {Array.isArray(firewallReport.public_ports_found)
                            ? firewallReport.public_ports_found.length
                            : 0}
                        </strong>
                      </div>
                      <div className="security-metric-card">
                        <span>Internal-Only Ports</span>
                        <strong>
                          {Array.isArray(
                            firewallReport.internal_only_ports_found,
                          )
                            ? firewallReport.internal_only_ports_found.length
                            : 0}
                        </strong>
                      </div>
                      <div className="security-metric-card">
                        <span>Unsafe Exposure</span>
                        <strong>
                          {Array.isArray(firewallReport.unsafe_exposure_found)
                            ? firewallReport.unsafe_exposure_found.length
                            : 0}
                        </strong>
                      </div>
                      <div className="security-metric-card">
                        <span>Safe Findings</span>
                        <strong>
                          {Array.isArray(
                            firewallReport.safe_configuration_found,
                          )
                            ? firewallReport.safe_configuration_found.length
                            : 0}
                        </strong>
                      </div>
                    </div>

                    <div className="admin-table-shell mb-4 overflow-x-auto">
                      <table className="admin-table min-w-max w-full">
                        <thead>
                          <tr>
                            <th className="min-w-[150px]">Area</th>
                            <th className="min-w-[320px]">Summary</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Public Ports</td>
                            <td>
                              {Array.isArray(
                                firewallReport.public_ports_found,
                              ) && firewallReport.public_ports_found.length > 0
                                ? firewallReport.public_ports_found
                                    .map(
                                      (item) =>
                                        `${item.port} (${item.local_address})`,
                                    )
                                    .join(", ")
                                : "No public ports recorded in the report."}
                            </td>
                          </tr>
                          <tr>
                            <td>Internal-Only Ports</td>
                            <td>
                              {Array.isArray(
                                firewallReport.internal_only_ports_found,
                              ) &&
                              firewallReport.internal_only_ports_found.length >
                                0
                                ? firewallReport.internal_only_ports_found
                                    .map(
                                      (item) =>
                                        `${item.port} (${item.local_address})`,
                                    )
                                    .join(", ")
                                : "No internal-only ports recorded in the report."}
                            </td>
                          </tr>
                          <tr>
                            <td>Unsafe Exposure</td>
                            <td>
                              {Array.isArray(
                                firewallReport.unsafe_exposure_found,
                              ) &&
                              firewallReport.unsafe_exposure_found.length > 0
                                ? firewallReport.unsafe_exposure_found.join(
                                    " | ",
                                  )
                                : "No unsafe exposure findings were recorded."}
                            </td>
                          </tr>
                          <tr>
                            <td>Safe Configuration</td>
                            <td>
                              {Array.isArray(
                                firewallReport.safe_configuration_found,
                              ) &&
                              firewallReport.safe_configuration_found.length > 0
                                ? firewallReport.safe_configuration_found.join(
                                    " | ",
                                  )
                                : "No safe configuration findings were recorded."}
                            </td>
                          </tr>
                          <tr>
                            <td>Recommended Fix</td>
                            <td>
                              {Array.isArray(
                                firewallReport.recommended_fixes,
                              ) && firewallReport.recommended_fixes.length > 0
                                ? firewallReport.recommended_fixes.join(" | ")
                                : "No recommended fixes recorded."}
                            </td>
                          </tr>
                          <tr>
                            <td>Errors</td>
                            <td>
                              {Array.isArray(firewallReport.errors) &&
                              firewallReport.errors.length > 0
                                ? firewallReport.errors.join(" | ")
                                : "No report errors recorded."}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="admin-table-shell overflow-x-auto">
                      <table className="admin-table min-w-max w-full">
                        <thead>
                          <tr>
                            <th className="min-w-[180px]">Command</th>
                            <th className="min-w-[100px]">Exit</th>
                            <th className="min-w-[420px]">
                              Raw Command Summary
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(Array.isArray(firewallReport.command_summaries)
                            ? firewallReport.command_summaries
                            : []
                          ).map((item) => (
                            <tr key={item.name}>
                              <td>{item.command}</td>
                              <td>{item.exit_code}</td>
                              <td className="whitespace-pre-wrap break-words">
                                {item.summary}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )
              )}
            </div>
          )}

          {/* Appraisal Report Section */}
          <div ref={appraisalReportRef} className="mb-6 sm:mb-8">
            <h2 className="mb-3 text-lg font-semibold sm:mb-4 sm:text-xl">
              Appraisal Report
            </h2>
            <div className="card mb-4 max-w-4xl rounded-[1.75rem] border border-border p-5 shadow-soft sm:mb-5 sm:p-7">
              <div className="mb-5 sm:mb-6">
                <h3 className="text-base font-semibold text-text sm:text-lg">
                  Generate Report
                </h3>
                <p className="mt-2 max-w-3xl text-sm leading-7 text-secondary-text sm:text-base">
                  Choose employee and date filters to generate a downloadable
                  appraisal report.
                </p>
              </div>
              <div className="space-y-4 sm:space-y-5">
                <div>
                  <label className="mb-2 block text-sm font-medium text-secondary-text sm:text-base">
                    Select Employee/Manager
                  </label>
                  <select
                    value={selectedUser}
                    onChange={handleUserSelect}
                    className="input-field w-full rounded-2xl px-4 py-3 text-sm text-text sm:text-base"
                  >
                    <option value="">Select a user</option>
                    {users
                      .filter((u) => u.role !== "admin")
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.email} ({u.role})
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-secondary-text sm:text-base">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={selectedStartDate}
                    onChange={handleStartDateChange}
                    className="input-field w-full rounded-2xl px-4 py-3 text-sm text-text sm:text-base"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-secondary-text sm:text-base">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={selectedEndDate}
                    onChange={handleEndDateChange}
                    className="input-field w-full rounded-2xl px-4 py-3 text-sm text-text sm:text-base"
                  />
                </div>
              </div>
              <button
                onClick={handleGenerateAppraisalReport}
                className="primary-btn mt-5 w-full justify-center rounded-2xl px-6 py-3 text-sm font-semibold sm:mt-6 sm:text-base"
              >
                Generate Appraisal Report PDF
              </button>
            </div>
            {appraisalReport && (
              <div className="card max-w-4xl p-5 sm:p-7">
                <h3 className="mb-4 text-base font-bold sm:text-lg">
                  Appraisal Report for {appraisalReport.name}
                </h3>
                <div className="mb-4 grid gap-2 text-xs sm:mb-5 sm:text-sm">
                  <p className="text-text">
                    <strong>User ID:</strong> {appraisalReport.user_id}
                  </p>
                  <p className="text-text">
                    <strong>Code:</strong> {appraisalReport.code}
                  </p>
                  <p className="text-text">
                    <strong>Role:</strong> {appraisalReport.role}
                  </p>
                </div>
                <div className="mb-4 sm:mb-5">
                  <strong className="text-xs sm:text-sm">
                    Efficiency Score:
                  </strong>
                  <div className="mt-2 h-2.5 w-full max-w-md rounded-full bg-gray-200">
                    <div
                      className="h-2.5 rounded-full bg-primary"
                      style={{ width: `${appraisalReport.efficiency_score}%` }}
                    ></div>
                  </div>
                  <span className="mt-1 inline-block text-xs text-text sm:text-sm">
                    {appraisalReport.efficiency_score}%
                  </span>
                </div>
                <div className="admin-table-shell mb-4 overflow-x-auto sm:mb-5">
                  <table className="admin-table min-w-max w-full">
                    <thead>
                      <tr>
                        <th className="min-w-[120px] sm:min-w-[150px]">
                          Metric
                        </th>
                        <th className="min-w-[120px] sm:min-w-[150px]">
                          Value
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Total Tasks</td>
                        <td>{appraisalReport.total_tasks}</td>
                      </tr>
                      <tr>
                        <td>Completed Tasks</td>
                        <td>{appraisalReport.completed_tasks}</td>
                      </tr>
                      <tr>
                        <td>Ongoing Tasks</td>
                        <td>{appraisalReport.ongoing_tasks}</td>
                      </tr>
                      <tr>
                        <td>Pending Approval Tasks</td>
                        <td>{appraisalReport.pending_approval_tasks}</td>
                      </tr>
                      <tr>
                        <td>Overdue Tasks</td>
                        <td>{appraisalReport.overdue_tasks}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {appraisalReport && (
                  <div>
                    <h4 className="mb-3 text-base font-semibold sm:text-lg">
                      Performance Chart
                    </h4>
                    <div className="chart-container w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg">
                      <canvas ref={chartRef} id="performanceChart"></canvas>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
